'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@savvy-kit/components/popover';
import { cn } from '@savvy-kit/lib/utils';
import { Tag } from '@savvy-kit/patterns/tag';
import { useIsPageTitleCollapsed } from '@savvy-kit/templates/page-title';
import { tagIndicatorPropsFromTagData } from 'src/utils/advisor-dash/tag-indicator-from-tag-data';

import type { HouseholdTagRow } from './household-tags-row';
import { visibleInlineTagCount } from './visible-inline-tag-count';

/**
 * Compact form of `HouseholdTagsRow` for the collapsed household header, where the tags share the
 * breadcrumb row with the trail and the action cluster. As many chips as measurably fit render
 * inline; the rest go behind a "+N" button.
 */

// Must track the `gap-1.5` on the chip row below — the fit is computed, not measured, so a change
// there silently skews the count rather than breaking anything visible.
const CHIP_GAP_PX = 6;
// Reserved before the button exists, so the fit never depends on whether it is currently rendered.
// Sized for "+99" — over-reserving costs at most one inline chip.
const OVERFLOW_BUTTON_WIDTH_PX = 44;

type HouseholdTagsInlineProps = {
  readonly tags: readonly HouseholdTagRow[];
  readonly disabled?: boolean;
  readonly onManage: () => void;
};

export function HouseholdTagsInline({ tags, disabled = false, onManage }: HouseholdTagsInlineProps) {
  const isCollapsed = useIsPageTitleCollapsed();

  const containerRef = useRef<HTMLDivElement>(null);
  const chipRowRef = useRef<HTMLDivElement>(null);
  const chipWidthsRef = useRef<readonly number[]>([]);

  const [containerWidth, setContainerWidth] = useState(0);
  const [overflowOpen, setOverflowOpen] = useState(false);

  // A measuring pass renders every chip so their natural widths can be cached. Without it the
  // widths would have to be read back off a row this component has already clipped, which reports
  // zero for the chips it dropped and makes the count unrecoverable once it shrinks.
  const [measuring, setMeasuring] = useState(true);
  const tagsKey = tags.map((tag) => tag.id).join('|');

  useLayoutEffect(() => {
    setMeasuring(true);
  }, [tagsKey]);

  useLayoutEffect(() => {
    if (!measuring) return;
    const row = chipRowRef.current;
    if (!row) return;
    chipWidthsRef.current = Array.from(row.children, (chip) => chip.getBoundingClientRect().width);
    setMeasuring(false);
  }, [measuring]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Seeded before observing: the observer's first callback lands after paint, which would
    // otherwise show a frame of "+N" with every chip dropped.
    setContainerWidth(el.clientWidth);
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The trigger lives in the breadcrumb row and fades out as the header expands, but the popover
  // content is portalled and would keep rendering against an invisible anchor.
  useEffect(() => {
    if (!isCollapsed) setOverflowOpen(false);
  }, [isCollapsed]);

  if (tags.length === 0) return null;

  const visibleCount = measuring
    ? tags.length
    : visibleInlineTagCount({
        chipWidths: chipWidthsRef.current,
        gapPx: CHIP_GAP_PX,
        availableWidth: containerWidth,
        overflowWidth: OVERFLOW_BUTTON_WIDTH_PX,
      });
  const hiddenTags = tags.slice(visibleCount);

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden transition-opacity duration-200',
        isCollapsed ? 'opacity-100' : 'opacity-0 pointer-events-none',
      )}
      aria-hidden={!isCollapsed}
      inert={!isCollapsed}
    >
      <div ref={chipRowRef} className="flex items-center gap-1.5">
        {tags.map((tag, index) => (
          <Tag
            key={tag.id}
            type="read-only"
            {...tagIndicatorPropsFromTagData({
              emoji: tag.emoji,
              hexColorCode: tag.hexColorCode,
              isSmart: tag.isSmart,
            })}
            className={cn('shrink-0 whitespace-nowrap border-0 bg-slate-200', index >= visibleCount && 'hidden')}
          >
            {tag.name}
          </Tag>
        ))}
      </div>

      {hiddenTags.length > 0 && (
        <Popover open={overflowOpen} onOpenChange={setOverflowOpen}>
          <PopoverTrigger
            render={
              <button
                type="button"
                aria-label={`Show ${hiddenTags.length} more ${hiddenTags.length === 1 ? 'tag' : 'tags'}`}
                className="shrink-0 rounded-md bg-slate-200 px-2 py-0.5 text-xs text-secondary transition-colors hover:bg-slate-300 cursor-pointer"
              />
            }
          >
            +{hiddenTags.length}
          </PopoverTrigger>
          <PopoverContent align="start" className="flex w-[260px] flex-col gap-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {hiddenTags.map((tag) => (
                <Tag
                  key={tag.id}
                  type="read-only"
                  {...tagIndicatorPropsFromTagData({
                    emoji: tag.emoji,
                    hexColorCode: tag.hexColorCode,
                    isSmart: tag.isSmart,
                  })}
                  className="border-0 bg-slate-200"
                >
                  {tag.name}
                </Tag>
              ))}
            </div>
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                setOverflowOpen(false);
                onManage();
              }}
              className="self-start text-sm text-cyan-400 hover:text-cyan-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              Manage tags
            </button>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
